import { Search, Star } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { MarketplaceAgent, MarketplaceSort } from "@/api/types";
import { EmptyState } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/badge";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ViewToggle, useViewMode } from "@/components/ui/view-toggle";
import { MarketplaceCard } from "@/features/marketplace/components/MarketplaceCard";
import { useMarketplace } from "@/features/marketplace/hooks/useMarketplace";

export function MarketplacePage() {
  const { t } = useTranslation("marketplace");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<MarketplaceSort>("rating");
  const [view, setView] = useViewMode("marketplace:view", "cards");
  const query = useMarketplace({ q: q.trim() || undefined, sort, limit: 50 });

  const columns = useMemo<DataTableColumn<MarketplaceAgent>[]>(
    () => [
      {
        key: "name",
        header: t("columns.name"),
        render: (a) => (
          <span className="font-medium text-ink-950">{a.name}</span>
        ),
      },
      {
        key: "description",
        header: t("columns.description"),
        className: "max-w-md truncate text-ink-500",
        render: (a) => a.description,
      },
      {
        key: "rating",
        header: t("columns.rating"),
        render: (a) =>
          a.rating_count > 0 ? (
            <span className="inline-flex items-center gap-1">
              <Star className="h-3 w-3 fill-current text-yellow-500" />
              {a.rating_avg?.toFixed(1)} ({a.rating_count})
            </span>
          ) : (
            "—"
          ),
      },
      {
        key: "installs",
        header: t("columns.installs"),
        render: (a) => a.install_count,
      },
      {
        key: "visibility",
        header: t("columns.visibility"),
        render: (a) => a.visibility,
      },
      {
        key: "installed",
        header: t("columns.installed"),
        render: (a) =>
          a.is_installed ? (
            <Badge variant="success">{t("badges.installed")}</Badge>
          ) : (
            "—"
          ),
      },
    ],
    [t],
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t("title")} description={t("subtitle")} />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("search")}
            className="pl-9"
          />
        </div>
        <Select
          value={sort}
          onValueChange={(v) => setSort(v as MarketplaceSort)}
        >
          <SelectTrigger className="w-[200px]" aria-label={t("sort.label")}>
            <SelectValue placeholder={t("sort.label")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="rating">{t("sort.rating")}</SelectItem>
            <SelectItem value="installs">{t("sort.installs")}</SelectItem>
            <SelectItem value="recent">{t("sort.recent")}</SelectItem>
          </SelectContent>
        </Select>
        <ViewToggle
          value={view}
          onChange={setView}
          labels={{ cards: t("viewCards"), list: t("viewList") }}
        />
      </div>

      {query.isLoading ? <LoadingState /> : null}
      {query.isError ? <ErrorState error={query.error} /> : null}
      {query.data && query.data.items.length === 0 ? (
        <EmptyState title={t("title")} description={t("empty")} />
      ) : null}
      {query.data && query.data.items.length > 0 ? (
        view === "list" ? (
          <DataTable
            columns={columns}
            rows={query.data.items}
            getRowKey={(a) => a.id}
            getRowHref={(a) => `/marketplace/${a.id}`}
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {query.data.items.map((agent) => (
              <MarketplaceCard key={agent.id} agent={agent} />
            ))}
          </div>
        )
      ) : null}
    </div>
  );
}
